import { Namespace, Relation, JourneysResource, PipelinesResource, ParcelResource } from './index.js';
/**
 * Adding new permissions for Custom Roles:
 * 1. Add the permission here in the appropriate permission group
 * 2. Update the hydra-permissions dependency in the UI repo
 * 3. The Custom Roles feature will automatically pick up the new permission
 *
 * NOTE: Before adding a new permission, have a quick discussion with the
 * Product Enablement team to determine whether it should go in an existing
 * permission group or if a new group needs to be created.
 */
// Feature groups map to the product nav so admins are already oriented when
// they land on the Custom Roles permission table. Each group exposes only
// the operational permissions (Execute, Publish, Send) that are relevant to
// the activities in that nav section.
// https://www.notion.so/custio/UI-Feedback-Consolidating-Permissions-2654302f4c2b8054a3cdc3a5c6c7233a
export var FeatureGroup;
(function (FeatureGroup) {
    FeatureGroup["ReportsAndActivity"] = "reports_and_activity";
    FeatureGroup["CreateContent"] = "create_content";
    FeatureGroup["SendMessages"] = "send_messages";
    FeatureGroup["ManageAudience"] = "manage_audience";
    FeatureGroup["ConfigureData"] = "configure_data";
    // Distinct from PermissionGroup.WorkspaceSettings ('workspace_settings') so
    // the two enums never collide at runtime when their values are mixed into
    // the same Set / Map / serialized payload.
    FeatureGroup["ConfigureWorkspace"] = "configure_workspace";
})(FeatureGroup || (FeatureGroup = {}));
// New grouped permission structure
export var PermissionGroup;
(function (PermissionGroup) {
    // Messaging & Content
    PermissionGroup["Campaigns"] = "campaigns";
    PermissionGroup["BroadcastsAndNewsletters"] = "broadcasts_and_newsletters";
    PermissionGroup["TransactionalMessages"] = "transactional_messages";
    PermissionGroup["AnonymousInAppMessages"] = "anonymous_in_app_messages";
    PermissionGroup["ContentLibraryAndDesignStudio"] = "content_library_and_design_studio";
    PermissionGroup["Deliveries"] = "deliveries";
    // People, Objects, and Activity
    PermissionGroup["People"] = "people";
    PermissionGroup["CustomObjects"] = "custom_objects";
    PermissionGroup["CustomObjectTypes"] = "custom_object_types";
    PermissionGroup["Segments"] = "segments";
    PermissionGroup["Geofences"] = "geofences";
    PermissionGroup["AdAudiences"] = "ad_audiences";
    PermissionGroup["Goals"] = "goals";
    PermissionGroup["GoalRevenueImpact"] = "goal_revenue_impact";
    PermissionGroup["ActivityLogs"] = "activity_logs";
    // Integrations, Imports, and Exports
    PermissionGroup["Integrations"] = "integrations";
    PermissionGroup["ImportData"] = "import_data";
    PermissionGroup["ExportData"] = "export_data";
    PermissionGroup["Collections"] = "collections";
    PermissionGroup["DataIndex"] = "data_index";
    // Workspace & Message Channel Configuration
    PermissionGroup["WorkspaceSettings"] = "workspace_settings";
    PermissionGroup["MessageChannelSettings"] = "message_channel_settings";
    PermissionGroup["SubscriptionCenterAndTopics"] = "subscription_center_and_topics";
    PermissionGroup["ConfigurationManager"] = "configuration_manager";
})(PermissionGroup || (PermissionGroup = {}));
// New grouped permission structure - maps permission groups to multiple individual permissions
export const permissionGroupPermissions = {
    [PermissionGroup.Campaigns]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.Campaigns}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.MetricExports}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.MetricExports}/${Relation.Create}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.Campaigns}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.TestEvents}/${Relation.Send}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Send}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Edit}`,
        ],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Campaigns}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Campaigns}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [`${Namespace.Journeys}/${JourneysResource.Campaigns}/${Relation.Execute}`],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.BroadcastsAndNewsletters]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.Broadcasts}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.Newsletters}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.MetricExports}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.MetricExports}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.Broadcasts}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Newsletters}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Send}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.Broadcasts}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Newsletters}/${Relation.Create}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.Broadcasts}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Newsletters}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [
            `${Namespace.Journeys}/${JourneysResource.Broadcasts}/${Relation.Execute}`,
            `${Namespace.Journeys}/${JourneysResource.Newsletters}/${Relation.Execute}`,
        ],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.TransactionalMessages]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.TransactionalMessages}/${Relation.View}`],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.TransactionalMessages}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Send}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Edit}`,
        ],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.TransactionalMessages}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.TransactionalMessages}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [`${Namespace.Journeys}/${JourneysResource.TransactionalMessages}/${Relation.Execute}`],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.AnonymousInAppMessages]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.InAppBroadcasts}/${Relation.View}`],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.InAppBroadcasts}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Send}`,
            `${Namespace.Journeys}/${JourneysResource.Messages}/${Relation.Edit}`,
        ],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.InAppBroadcasts}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.InAppBroadcasts}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.ContentLibraryAndDesignStudio]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.Snippets}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.Layouts}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.Assets}/${Relation.View}`,
            `${Namespace.Parcel}/${ParcelResource.Emails}/${Relation.View}`,
            `${Namespace.Parcel}/${ParcelResource.Components}/${Relation.View}`,
            `${Namespace.Parcel}/${ParcelResource.WhatsApp}/${Relation.View}`,
            `${Namespace.Parcel}/${ParcelResource.Sms}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.Snippets}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Layouts}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Assets}/${Relation.Edit}`,
            `${Namespace.Parcel}/${ParcelResource.Emails}/${Relation.Edit}`,
            `${Namespace.Parcel}/${ParcelResource.Components}/${Relation.Edit}`,
            `${Namespace.Parcel}/${ParcelResource.Previews}/${Relation.SendTest}`,
            `${Namespace.Parcel}/${ParcelResource.WhatsApp}/${Relation.Edit}`,
            `${Namespace.Parcel}/${ParcelResource.Sms}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.Snippets}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Layouts}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Assets}/${Relation.Create}`,
            `${Namespace.Parcel}/${ParcelResource.Emails}/${Relation.Create}`,
            `${Namespace.Parcel}/${ParcelResource.Components}/${Relation.Create}`,
            `${Namespace.Parcel}/${ParcelResource.WhatsApp}/${Relation.Create}`,
            `${Namespace.Parcel}/${ParcelResource.Sms}/${Relation.Create}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.Snippets}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Layouts}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Assets}/${Relation.Delete}`,
            `${Namespace.Parcel}/${ParcelResource.Emails}/${Relation.Delete}`,
            `${Namespace.Parcel}/${ParcelResource.Components}/${Relation.Delete}`,
            `${Namespace.Parcel}/${ParcelResource.WhatsApp}/${Relation.Delete}`,
            `${Namespace.Parcel}/${ParcelResource.Sms}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [`${Namespace.Journeys}/${JourneysResource.Content}/${Relation.Publish}`],
        [Relation.Manage]: [],
    },
    [PermissionGroup.People]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.People}/${Relation.View}`],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.People}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Subscriptions}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.People}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Subscriptions}/${Relation.Edit}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.People}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Suppressions}/${Relation.Create}`,
            `${Namespace.Pipelines}/${PipelinesResource.Suppressions}/${Relation.View}`,
            `${Namespace.Pipelines}/${PipelinesResource.Suppressions}/${Relation.Create}`,
            `${Namespace.Pipelines}/${PipelinesResource.Suppressions}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.CustomObjects]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.Objects}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.ObjectRelationships}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.Objects}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.ObjectRelationships}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.Objects}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.ObjectRelationships}/${Relation.Create}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.Objects}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.ObjectRelationships}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.CustomObjectTypes]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.ObjectTypes}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.ObjectTypes}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.ObjectTypes}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.ObjectTypes}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Segments]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Segments}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Segments}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Segments}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Segments}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Geofences]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Geofences}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Geofences}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Geofences}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Geofences}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.AdAudiences]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.AdAudience}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.AdAudience}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.AdAudience}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.AdAudience}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Goals]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Goals}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Goals}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Goals}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Goals}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.GoalRevenueImpact]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.GoalsRevenue}/${Relation.View}`],
        [Relation.Edit]: [],
        [Relation.Create]: [],
        [Relation.Delete]: [],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.ActivityLogs]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Logs}/${Relation.View}`],
        [Relation.Edit]: [],
        [Relation.Create]: [],
        [Relation.Delete]: [],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Integrations]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.SegmentIntegration}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.JsSnippets}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.Integrations}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.AudiencesIntegration}/${Relation.View}`,
            `${Namespace.Pipelines}/${PipelinesResource.Sources}/${Relation.View}`,
            `${Namespace.Pipelines}/${PipelinesResource.Destinations}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.SqlSyncs}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.Forms}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.SegmentIntegration}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.JsSnippets}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.MParticleIntegration}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.AudiencesIntegration}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Integrations}/${Relation.Edit}`,
            `${Namespace.Pipelines}/${PipelinesResource.Sources}/${Relation.Edit}`,
            `${Namespace.Pipelines}/${PipelinesResource.Destinations}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.SqlSyncs}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.Forms}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.ReportingWebhooks}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.SegmentIntegration}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.JsSnippets}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Integrations}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.AudiencesIntegration}/${Relation.Create}`,
            `${Namespace.Pipelines}/${PipelinesResource.Sources}/${Relation.Create}`,
            `${Namespace.Pipelines}/${PipelinesResource.Destinations}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.SqlSyncs}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.Forms}/${Relation.Create}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.SegmentIntegration}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.JsSnippets}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.MParticleIntegration}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.AudiencesIntegration}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Integrations}/${Relation.Delete}`,
            `${Namespace.Pipelines}/${PipelinesResource.Sources}/${Relation.Delete}`,
            `${Namespace.Pipelines}/${PipelinesResource.Destinations}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.SqlSyncs}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.Forms}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.ReportingWebhooks}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.ImportData]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Imports}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Imports}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Imports}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Imports}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.ExportData]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Exports}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Exports}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Exports}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Exports}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Collections]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Collections}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.Collections}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.Collections}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Collections}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.DataIndex]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.AttributeData}/${Relation.View}`,
            `${Namespace.Pipelines}/${PipelinesResource.AttributeData}/${Relation.View}`,
        ],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.AttributeMetadata}/${Relation.Edit}`],
        [Relation.Create]: [],
        [Relation.Delete]: [],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.WorkspaceSettings]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.WorkspaceSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.SendingOptions}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.MessageSettings}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.WorkspaceSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.MergeOptions}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.SendingOptions}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.MessageSettings}/${Relation.Edit}`,
        ],
        [Relation.Create]: [],
        [Relation.Delete]: [],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.MessageChannelSettings]: {
        [Relation.View]: [
            `${Namespace.Journeys}/${JourneysResource.EmailMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.SlackMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.TwillioMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.PushMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.InAppMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.WhatsAppMessageSettings}/${Relation.View}`,
            `${Namespace.Journeys}/${JourneysResource.LanguageSettings}/${Relation.View}`,
        ],
        [Relation.Edit]: [
            `${Namespace.Journeys}/${JourneysResource.EmailMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.SlackMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.TwillioMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.PushMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.InAppMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.WhatsAppMessageSettings}/${Relation.Edit}`,
            `${Namespace.Journeys}/${JourneysResource.LanguageSettings}/${Relation.Edit}`,
        ],
        [Relation.Create]: [
            `${Namespace.Journeys}/${JourneysResource.EmailMessageSettings}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.SlackMessageSettings}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.TwillioMessageSettings}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.PushMessageSettings}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.InAppMessageSettings}/${Relation.Create}`,
            `${Namespace.Journeys}/${JourneysResource.LanguageSettings}/${Relation.Create}`,
        ],
        [Relation.Delete]: [
            `${Namespace.Journeys}/${JourneysResource.EmailMessageSettings}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.SlackMessageSettings}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.TwillioMessageSettings}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.PushMessageSettings}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.InAppMessageSettings}/${Relation.Delete}`,
            `${Namespace.Journeys}/${JourneysResource.LanguageSettings}/${Relation.Delete}`,
        ],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.SubscriptionCenterAndTopics]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.SubscriptionCenter}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.SubscriptionCenter}/${Relation.Edit}`],
        [Relation.Create]: [],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.SubscriptionCenter}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.ConfigurationManager]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.ConfigurationManager}/${Relation.View}`],
        [Relation.Edit]: [`${Namespace.Journeys}/${JourneysResource.ConfigurationManager}/${Relation.Edit}`],
        [Relation.Create]: [`${Namespace.Journeys}/${JourneysResource.ConfigurationManager}/${Relation.Create}`],
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.ConfigurationManager}/${Relation.Delete}`],
        [Relation.Send]: [],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
    [PermissionGroup.Deliveries]: {
        [Relation.View]: [`${Namespace.Journeys}/${JourneysResource.Deliveries}/${Relation.View}`],
        [Relation.Edit]: [],
        [Relation.Create]: [],
        // Manage is the backend permission that gates DELETE on delivery records;
        // it surfaces in the UI through the standard Delete column on the
        // Deliveries row rather than its own Manage column.
        [Relation.Delete]: [`${Namespace.Journeys}/${JourneysResource.Deliveries}/${Relation.Manage}`],
        [Relation.Send]: [`${Namespace.Journeys}/${JourneysResource.Deliveries}/${Relation.Send}`],
        [Relation.SendTest]: [],
        [Relation.Enable]: [],
        [Relation.Execute]: [],
        [Relation.Publish]: [],
        [Relation.Manage]: [],
    },
};
/**
 * Get all permissions for a specific permission group
 */
export function getPermissionsByPermissionGroup(permissionGroup) {
    return permissionGroupPermissions[permissionGroup] || null;
}
export const featureGroupPermissions = {
    [FeatureGroup.ReportsAndActivity]: [
        PermissionGroup.Goals,
        PermissionGroup.GoalRevenueImpact,
        PermissionGroup.ActivityLogs,
        PermissionGroup.Deliveries,
    ],
    [FeatureGroup.CreateContent]: [PermissionGroup.ContentLibraryAndDesignStudio],
    [FeatureGroup.SendMessages]: [
        PermissionGroup.Campaigns,
        PermissionGroup.BroadcastsAndNewsletters,
        PermissionGroup.TransactionalMessages,
        PermissionGroup.AnonymousInAppMessages,
    ],
    [FeatureGroup.ManageAudience]: [
        PermissionGroup.People,
        PermissionGroup.CustomObjects,
        PermissionGroup.CustomObjectTypes,
        PermissionGroup.Segments,
        PermissionGroup.Geofences,
        PermissionGroup.AdAudiences,
    ],
    [FeatureGroup.ConfigureData]: [
        PermissionGroup.Integrations,
        PermissionGroup.ImportData,
        PermissionGroup.ExportData,
        PermissionGroup.Collections,
        PermissionGroup.DataIndex,
    ],
    [FeatureGroup.ConfigureWorkspace]: [
        PermissionGroup.WorkspaceSettings,
        PermissionGroup.MessageChannelSettings,
        PermissionGroup.SubscriptionCenterAndTopics,
        PermissionGroup.ConfigurationManager,
    ],
};
//# sourceMappingURL=permission-groups.js.map